import React from 'react';
import { motion } from 'framer-motion';
import { Wifi } from 'lucide-react';
import ISPFeatureItem from './ISPFeatureItem';

/**
 * ISPFeaturesContent component
 * Displays the ISP features content tab
 */
const ISPFeaturesContent = ({ contentVariants, expandedFeatures, toggleFeature }) => {
  // ISP Features data
  const ispFeatures = [
    {
      id: 'uncapped',
      title: 'Uncapped',
      icon: <Wifi className="h-6 w-6 text-purple-400" />,
      shortDescription: 'No data limits on your internet connection',
      fullDescription: 'Uncapped means you have unlimited data usage with no hard caps. You can download, stream, and browse as much as you want without worrying about running out of data or incurring additional charges. This is perfect for households with multiple users, streaming enthusiasts, and remote workers who need constant, reliable internet access.'
    },
    {
      id: 'unshaped',
      title: 'Unshaped',
      icon: <Wifi className="h-6 w-6 text-blue-400" />,
      shortDescription: 'Equal priority for all types of internet traffic',
      fullDescription: 'Unshaped means we don\'t prioritize certain types of internet traffic over others. Many ISPs "shape" traffic by slowing down specific services like streaming or gaming during peak hours. Our unshaped service ensures all your online activities receive equal priority and consistent speeds, whether you\'re streaming Netflix, gaming online, or on a video call.'
    },
    {
      id: 'unthrottled',
      title: 'Unthrottled',
      icon: <Wifi className="h-6 w-6 text-green-400" />,
      shortDescription: 'Consistent speeds without artificial slowdowns',
      fullDescription: "Unthrottled means we never intentionally slow down your connection, regardless of how much data you use. Some ISPs throttle (reduce speeds) after you reach certain usage thresholds or during peak hours. With our unthrottled service, you'll experience the full speed of your package at all times, ensuring smooth streaming, responsive gaming, and efficient downloads even during peak usage periods."
    },
    {
      id: 'unlimited',
      title: 'Unlimited',
      icon: <Wifi className="h-6 w-6 text-pink-400" />,
      shortDescription: 'No restrictions on your internet usage',
      fullDescription: "Unlimited means there are no restrictions on how you use your internet connection. You can connect as many devices as you want, run servers if needed, and use any online service without limitations. We don't impose fair usage policies that restrict your activities or penalize you for heavy usage. Your internet, your rules - that's our unlimited promise."
    }
  ];

  return (
    <motion.div
      key="isp-content"
      className="p-4 md:p-8"
      variants={contentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-2">Our Premium ISP Features</h3>
          <p className="text-gray-300 max-w-3xl mx-auto">
            We believe in providing truly unlimited internet without restrictions.
            Here's what makes our service stand out from the competition.
          </p>
        </div>
        
        {/* Promotional Savings Banner */}
        <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 rounded-xl p-6 mb-10 border border-purple-500/30">
          <div className="text-center mb-4">
            <h3 className="text-3xl font-bold text-white">
              Save up to <span className="text-green-400">R5,000.00</span>
            </h3>
            <p className="text-xl text-gray-300 mt-1">
              With our exclusive free services and equipment
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-purple-900/30 rounded-lg p-4 text-center border border-purple-500/30">
              <div className="text-2xl font-bold text-white mb-1">Free Installation</div>
              <div className="text-green-400 font-bold text-xl">R2,000 Value</div>
              <p className="text-gray-300 mt-2 text-sm">
                Professional installation at no extra cost
              </p>
            </div>
            
            <div className="bg-blue-900/30 rounded-lg p-4 text-center border border-blue-500/30">
              <div className="text-2xl font-bold text-white mb-1">Free Activation</div>
              <div className="text-green-400 font-bold text-xl">R1,000 Value</div>
              <p className="text-gray-300 mt-2 text-sm">
                No activation fees to get connected
              </p>
            </div>
            
            <div className="bg-indigo-900/30 rounded-lg p-4 text-center border border-indigo-500/30">
              <div className="text-2xl font-bold text-white mb-1">Free Router</div>
              <div className="text-green-400 font-bold text-xl">R2,000 Value</div>
              <p className="text-gray-300 mt-2 text-sm">
                Premium mesh-capable WiFi 6 router included
              </p>
            </div>
          </div>
        </div>
        
        {/* ISP Features */}
        <div className="space-y-6">
          {ispFeatures.map((feature) => (
            <ISPFeatureItem
              key={feature.id}
              feature={feature}
              isExpanded={expandedFeatures[feature.id]}
              toggleFeature={toggleFeature}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default ISPFeaturesContent;